import React from "react";
import { createPortal } from "react-dom";
import { getReactPortalRoot } from "../utils/reactPortalRoot.js";
import ConfirmationModal from "./ConfirmationModal.jsx";
import "./MobileWorkspace.css";

export function MobileEditor({ title, children, footer, onClose, dirty = false, busy = false }) {
  const panelRef = React.useRef(null);
  const [confirmDiscard, setConfirmDiscard] = React.useState(false);
  const titleId = React.useId();
  const discardFocusRef = React.useRef(null);
  const requestClose = () => {
    if (busy) return;
    if (dirty) {
      discardFocusRef.current = document.activeElement;
      setConfirmDiscard(true);
    }
    else onClose();
  };

  React.useEffect(() => {
    if (panelRef.current) panelRef.current.inert = confirmDiscard;
    if (!confirmDiscard && discardFocusRef.current?.isConnected) {
      discardFocusRef.current.focus({ preventScroll: true });
      discardFocusRef.current = null;
    }
  }, [confirmDiscard]);

  React.useEffect(() => {
    const previousFocus = document.activeElement;
    const app = document.querySelector('[data-react-app-root]');
    const previousInert = app?.inert;
    const previousOverflow = document.documentElement.style.overflow;
    if (app) app.inert = true;
    document.documentElement.style.overflow = "hidden";
    panelRef.current?.focus();
    const viewport = window.visualViewport;
    const syncViewport = () => {
      panelRef.current?.style.setProperty("--mobile-viewport-height", `${viewport?.height || window.innerHeight}px`);
      panelRef.current?.style.setProperty("--mobile-viewport-top", `${viewport?.offsetTop || 0}px`);
    };
    syncViewport();
    viewport?.addEventListener("resize", syncViewport);
    viewport?.addEventListener("scroll", syncViewport);
    return () => {
      if (app) app.inert = previousInert;
      document.documentElement.style.overflow = previousOverflow;
      viewport?.removeEventListener("resize", syncViewport);
      viewport?.removeEventListener("scroll", syncViewport);
      if (previousFocus?.isConnected) previousFocus.focus?.({ preventScroll: true });
    };
  }, []);

  return createPortal(
    <>
      <section ref={panelRef} tabIndex={-1} role="dialog" aria-modal="true" aria-busy={busy} aria-labelledby={titleId} className="mobile-editor" onKeyDownCapture={(event) => {
        if (!panelRef.current?.contains(event.target)) return;
        if (event.key === "Escape") { event.preventDefault(); event.stopPropagation(); requestClose(); }
      }} onKeyDown={(event) => {
        if (!panelRef.current?.contains(event.target)) return;
        if (event.key !== "Tab") return;
        const targets = [...panelRef.current.querySelectorAll('button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled), a[href], [tabindex="0"]')].filter((element) => element.getClientRects().length);
        const first = targets[0];
        const last = targets[targets.length - 1];
        if (event.shiftKey && (document.activeElement === first || document.activeElement === panelRef.current)) { event.preventDefault(); last?.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
      }}>
        <header><h2 id={titleId}>{title}</h2><button type="button" disabled={busy} onClick={requestClose}>Fermer</button></header>
        <div className="mobile-editor-content">{children}</div>
        {footer ? <footer>{footer}<button type="button" disabled={busy} onClick={requestClose}>Annuler</button></footer> : null}
      </section>
      {confirmDiscard ? <ConfirmationModal title="Annuler les modifications ?" description="Les changements non enregistrés de cette fiche seront perdus." cornerRadius="grain" confirmLabel="Abandonner les modifications" onCancel={() => setConfirmDiscard(false)} onClose={() => setConfirmDiscard(false)} onConfirm={onClose} /> : null}
    </>,
    getReactPortalRoot(),
  );
}

export default function MobileRecordCard({ title, summary, children, editing, canEdit = true, pending = false, dirty = false, onEdit, onCancel, onSave, canSave = true, feedback = "", recordId }) {
  const savingRef = React.useRef(false);
  const [saving, setSaving] = React.useState(false);
  const [saveError, setSaveError] = React.useState("");
  const busy = pending || saving;
  React.useEffect(() => { if (!editing) setSaveError(""); }, [editing]);
  const save = async () => {
    if (busy || savingRef.current || !canSave) return;
    savingRef.current = true;
    setSaving(true);
    setSaveError("");
    try {
      await onSave();
    } catch (error) {
      setSaveError(error?.message || "L’enregistrement a échoué. Réessayez sans quitter cette fiche.");
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };
  return (
    <article className="mobile-record-card" data-mobile-record={recordId}>
      <h3>{title}</h3>
      <div className="mobile-record-summary">{summary}</div>
      <div className="mobile-record-actions">
        {canEdit ? <button type="button" disabled={pending} onClick={onEdit}>Modifier</button> : null}
        {!editing ? <details><summary>Voir les détails</summary><div className="mobile-record-fields">{children}</div></details> : null}
      </div>
      {editing ? <MobileEditor title={title} dirty={dirty} busy={busy} onClose={onCancel} footer={<>
        {saveError || feedback ? <p className="mobile-record-feedback" role={saveError ? "alert" : "status"}>{saveError || feedback}</p> : null}
        <button className="mobile-primary-action" type="button" disabled={busy || !canSave} onClick={save}>{busy ? "Enregistrement…" : "Enregistrer"}</button>
      </>}>
        <fieldset disabled={busy} className="mobile-record-fields">{children}</fieldset>
      </MobileEditor> : null}
    </article>
  );
}
